import styled from '@emotion/styled'
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useRef } from 'react'
import ReactTooltip from 'react-tooltip'

import { disabledStyles, styles } from './Button.styles'

import {
  BUTTON_SIZE_LG,
  BUTTON_SIZE_MD,
  BUTTON_SIZE_SM,
  BUTTON_VARIANT_DANGER,
  BUTTON_VARIANT_NEGATIVE,
  BUTTON_VARIANT_NEUTRAL,
  BUTTON_VARIANT_PRIMARY,
  BUTTON_VARIANT_SECONDARY
} from './Button.constants'

export * from './Button.constants'


const StyledButton = styled.button`
  ${({ kind, size, disabled }) => `
    ${styles(kind, size)}
    ${disabled ? disabledStyles(kind) : ''}
  `}
`
const StyledA = styled.a`
  ${({ kind, size, disabled }) => `
    ${styles(kind, size)}
    ${disabled ? disabledStyles(kind) : ''}
  `}
`

export const Button = ({
  children,
  className,
  iconClasses,
  disabled,
  href,
  target,
  kind,
  isLink,
  onClick,
  size,
  tooltip,
  type,
  'data-mu-test': dataAttributeTest
}) => {
  const elRef = useRef(null)

  useEffect(() => {
    if (!tooltip) {
      return
    }

    const cleanup = () => {
      ReactTooltip.hide(elRef.current)
    }

    return cleanup
  }, [tooltip])

  const props = {
    disabled,
    onClick,
    className: classnames(
      'e-button',
      {
        [`e-button--${kind}`]: kind,
        [`e-button--${size}`]: size
      },
      className
    ),
    ref: elRef,
    'data-tip': tooltip,
    'data-mu-test': classnames('button', dataAttributeTest),
    kind,
    size
  }

  const Content = () => {
    return children ? (
      <span>
        {children}
        {!!iconClasses && (
          <span className={classnames('e-button__icon', iconClasses)} />
        )}
      </span>
    ) : iconClasses ? (
      <span className={classnames('e-button__icon', iconClasses)} />
    ) : null
  }

  if (isLink) {
    return (
      <StyledA {...props} href={href} target={target}>
        <Content />
      </StyledA>
    )
  }

  return (
    <StyledButton {...props} type={type}>
      <Content />
    </StyledButton>
  )
}

Button.defaultProps = {
  disabled: false,
  isLink: false
}

Button.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  iconClasses: PropTypes.string,
  disabled: PropTypes.bool,
  href: PropTypes.string,
  kind: PropTypes.oneOf([
    BUTTON_VARIANT_PRIMARY,
    BUTTON_VARIANT_SECONDARY,
    BUTTON_VARIANT_NEUTRAL,
    BUTTON_VARIANT_DANGER,
    BUTTON_VARIANT_NEGATIVE
  ]),
  isLink: PropTypes.bool,
  onClick: PropTypes.func,
  size: PropTypes.oneOf([BUTTON_SIZE_SM, BUTTON_SIZE_MD, BUTTON_SIZE_LG]),
  target: PropTypes.string,
  tooltip: PropTypes.string,
  /**
   * Additional values for data-attribute used for tests and tracking
   **/
  'data-mu-test': PropTypes.string
}
